/**
 * Markdown ⇄ tiptap document conversion for the rich note/reason editor.
 *
 * Markdown stays the stored format — the database, the revision trail and the
 * read renderer (components/NoteMarkdown.tsx) never see tiptap JSON. The
 * editor is only an editing surface: it opens from `markdownToDoc(text)` and
 * saves whatever `docToMarkdown(json)` returns. Both sides speak the same
 * deliberately tiny subset as lib/noteMarkdown.ts; anything the subset cannot
 * express (nested lists, ordered lists) is flattened rather than invented.
 */

import {
	type BlockNode,
	type InlineNode,
	isSafeLinkHref,
	parseNoteMarkdown,
} from "./noteMarkdown";

/** Minimal structural type for tiptap's JSON — enough to build and walk. */
export type RichDocNode = {
	type: string;
	attrs?: Record<string, unknown>;
	marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
	content?: RichDocNode[];
	text?: string;
};

type MarkState = { bold?: boolean; italic?: boolean; linkHref?: string };

function inlineToTextNodes(
	nodes: InlineNode[],
	state: MarkState,
): RichDocNode[] {
	const out: RichDocNode[] = [];
	for (const node of nodes) {
		switch (node.kind) {
			case "text": {
				if (node.text === "") break;
				const marks: RichDocNode["marks"] = [];
				if (state.bold) marks.push({ type: "bold" });
				if (state.italic) marks.push({ type: "italic" });
				if (state.linkHref)
					marks.push({ type: "link", attrs: { href: state.linkHref } });
				out.push(
					marks.length
						? { type: "text", text: node.text, marks }
						: { type: "text", text: node.text },
				);
				break;
			}
			case "bold":
				out.push(...inlineToTextNodes(node.children, { ...state, bold: true }));
				break;
			case "italic":
				out.push(
					...inlineToTextNodes(node.children, { ...state, italic: true }),
				);
				break;
			case "link":
				out.push(
					...inlineToTextNodes([{ kind: "text", text: node.label }], {
						...state,
						linkHref: node.href,
					}),
				);
				break;
		}
	}
	return out;
}

function blockToDocNode(block: BlockNode): RichDocNode {
	switch (block.kind) {
		case "heading":
			return {
				type: "heading",
				attrs: { level: block.level },
				content: inlineToTextNodes(block.children, {}),
			};
		case "bullets":
			return {
				type: "bulletList",
				content: block.items.map((item) => ({
					type: "listItem",
					content: [{ type: "paragraph", content: inlineToTextNodes(item, {}) }],
				})),
			};
		case "tasks":
			return {
				type: "taskList",
				content: block.items.map((item) => ({
					type: "taskItem",
					attrs: { checked: item.checked },
					content: [
						{ type: "paragraph", content: inlineToTextNodes(item.children, {}) },
					],
				})),
			};
		case "paragraph": {
			const content: RichDocNode[] = [];
			block.lines.forEach((line, index) => {
				if (index > 0) content.push({ type: "hardBreak" });
				content.push(...inlineToTextNodes(line, {}));
			});
			return { type: "paragraph", content };
		}
	}
}

export function markdownToDoc(text: string): RichDocNode {
	const blocks = parseNoteMarkdown(text);
	return {
		type: "doc",
		content: blocks.length
			? blocks.map(blockToDocNode)
			: [{ type: "paragraph" }],
	};
}

/**
 * Wrap `text` in a marker pair, unless the text itself contains the marker —
 * then the styling is dropped rather than emitting markdown that would parse
 * back differently.
 */
function wrapUnless(text: string, marker: string, closing = marker): string {
	if (text.includes(marker) || text.includes(closing)) return text;
	return marker + text + closing;
}

function textNodeToMarkdown(node: RichDocNode): string {
	let text = node.text ?? "";
	if (text === "") return "";
	const markTypes = new Set((node.marks ?? []).map((mark) => mark.type));
	if (markTypes.has("italic")) {
		// `_` unless the text contains one; then `*`, which the parser also reads.
		text = text.includes("_") ? wrapUnless(text, "*") : `_${text}_`;
	}
	if (markTypes.has("bold")) text = wrapUnless(text, "**");
	const link = (node.marks ?? []).find((mark) => mark.type === "link");
	const href = typeof link?.attrs?.href === "string" ? link.attrs.href : "";
	// The label must not contain `]` and the parser requires a paren/space-free
	// safe href — otherwise keep the plain label, exactly like the renderer.
	if (href && isSafeLinkHref(href) && !/[()\s]/.test(href) && !text.includes("]")) {
		text = `[${text}](${href})`;
	}
	return text;
}

/** Serialize inline content: text runs, with hardBreak as a real newline. */
function inlineContentToMarkdown(content: RichDocNode[] | undefined): string {
	return (content ?? [])
		.map((node) =>
			node.type === "hardBreak" ? "\n" : textNodeToMarkdown(node),
		)
		.join("");
}

/**
 * A list item's paragraphs, flattened to single-line text. Nested lists that
 * tiptap allows but the subset cannot express are surfaced as extra items by
 * the caller.
 */
function listItemLines(
	item: RichDocNode,
	pushLine: (text: string) => void,
	pushNested: (list: RichDocNode) => void,
): void {
	const texts: string[] = [];
	for (const child of item.content ?? []) {
		if (child.type === "paragraph") {
			const text = inlineContentToMarkdown(child.content).replace(/\n/g, " ");
			if (text.trim() !== "") texts.push(text);
		} else if (child.type === "bulletList" || child.type === "taskList") {
			pushNested(child);
		}
	}
	pushLine(texts.join(" "));
}

function listToMarkdownLines(list: RichDocNode, lines: string[]): void {
	for (const item of list.content ?? []) {
		const checked = item.attrs?.checked === true;
		const prefix =
			item.type === "taskItem" ? (checked ? "- [x] " : "- [ ] ") : "- ";
		const nested: RichDocNode[] = [];
		listItemLines(
			item,
			(text) => {
				if (text.trim() !== "" || item.type === "taskItem") {
					lines.push(prefix + text);
				}
			},
			(child) => nested.push(child),
		);
		for (const child of nested) listToMarkdownLines(child, lines);
	}
}

export function docToMarkdown(doc: RichDocNode): string {
	const blocks: string[] = [];
	for (const node of doc.content ?? []) {
		switch (node.type) {
			case "heading": {
				const level =
					typeof node.attrs?.level === "number" ? node.attrs.level : 1;
				const text = inlineContentToMarkdown(node.content).replace(/\n/g, " ");
				if (text.trim() !== "") {
					blocks.push(`${"#".repeat(Math.min(Math.max(level, 1), 6))} ${text}`);
				}
				break;
			}
			case "bulletList":
			case "taskList":
			case "orderedList": {
				const lines: string[] = [];
				listToMarkdownLines(node, lines);
				if (lines.length) blocks.push(lines.join("\n"));
				break;
			}
			case "paragraph": {
				const text = inlineContentToMarkdown(node.content);
				if (text.trim() !== "") blocks.push(text);
				break;
			}
			default:
				break;
		}
	}
	return blocks.join("\n\n");
}
